import { Component, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { ModalData } from '@datachain/ui-sdk/components/app-modal';
import { Store } from '@ngrx/store';
import {
	BehaviorSubject,
	debounceTime,
	firstValueFrom,
	map,
	Observable,
	ReplaySubject,
	takeUntil,
	tap,
} from 'rxjs';

import { DcIcons } from '../../../../ui/app-dc.icons';
import { AccessConfigEntity } from '../../../domain/exposition-access-config.entity';
import {
	ExpositionDiagnosticError,
	ExpositionErrorKey,
} from '../../../domain/exposition-diagnostic-errors.entity';
import { ExpositionProvider } from '../../../providers/exposition.provider';
import {
	closeEditExpositionApiModal,
	createExpositionAccess,
	ExpositionsSelector,
	initExpositionAccessCreation,
	initExpositionAccessModification,
	saveExpositionAccess,
} from '../../../store';
import { ExpositionAccessConfigComponent } from '../../exposition-access-config/exposition-access-config.component';

@Component({
	selector: 'app-exposition-access-modal',
	templateUrl: './exposition-access-modal.component.html',
	styleUrls: ['./exposition-access-modal.component.scss'],
	providers: [ExpositionProvider],
})
export class ExpositionAccessModalComponent<
	T extends { expositionId: number; accessId: string }
> extends DcBaseComponent {
	public readonly Icons = DcIcons;
	private readonly access$ = new ReplaySubject<AccessConfigEntity>(1);
	private readonly isNew$ = new BehaviorSubject<boolean>(true);
	public vo$: Observable<{
		access: AccessConfigEntity;
		isNewConfig: boolean;
		canEdit: boolean;
		currentError: ExpositionDiagnosticError | undefined;
	}>;

	@ViewChild(ExpositionAccessConfigComponent)
	private readonly accessConfigCmp: ExpositionAccessConfigComponent | null = null;

	protected readonly DcIcons = DcIcons;
	protected readonly ExpositionErrorKey = ExpositionErrorKey;
	public constructor(
		private readonly store: Store,
		private readonly expositionsSelector: ExpositionsSelector,
		private readonly expositionProvider: ExpositionProvider,
		@Inject(MAT_DIALOG_DATA) public readonly modalData: ModalData & T
	) {
		super();
		this.cmpId = 'edit-exposition-access';

		if (this.modalData.accessId === '-1') {
			this.store.dispatch(
				initExpositionAccessCreation({
					expositionId: this.modalData.expositionId,
				})
			);
		} else {
			this.store.dispatch(
				initExpositionAccessModification({
					expositionId: this.modalData.expositionId,
					accessId: this.modalData.accessId,
				})
			);
		}

		this.expositionsSelector
			.getAccessConfig$()
			.pipe(
				debounceTime(50),
				takeUntil(this.onDestroy$),
				tap((access) => {
					this.isNew$.next(access.id === '');
					this.access$.next(access);
				})
			)
			.subscribe();

		this.vo$ = combineAll({
			access: this.access$,
			isNewConfig: this.isNew$,
			canEdit: this.expositionsSelector.getCanEditExposition$(),
			currentError: this.expositionProvider.errors$.pipe(
				map((errors) => errors.first())
			),
		});
	}

	public close(): void {
		this.store.dispatch(
			closeEditExpositionApiModal({
				expositionId: this.modalData.expositionId,
			})
		);
	}

	public async save(): Promise<void> {
		if (!this.accessConfigCmp) {
			return;
		}
		const isNew = await firstValueFrom(this.isNew$);
		const access = this.accessConfigCmp.getAccessConfig();

		if (isNew) {
			this.store.dispatch(
				createExpositionAccess({
					expositionId: this.modalData.expositionId,
					access,
				})
			);
		} else {
			this.store.dispatch(
				saveExpositionAccess({
					expositionId: this.modalData.expositionId,
					access,
				})
			);
		}
	}
}
